export const MESSAGE_STATUS = {
  FAILED: 'failed',
  SENT: 'sent',
  DELIVERED: 'delivered',
  READ: 'read',
  PROGRESS: 'progress',
};

export const MESSAGE_TYPE = {
  INCOMING: 0,
  OUTGOING: 1,
  ACTIVITY: 2,
  TEMPLATE: 3,
};

export const CONVERSATION_STATUS = {
  OPEN: 'open',
  RESOLVED: 'resolved',
  PENDING: 'pending',
  SNOOZED: 'snoozed',
};

export const CONVERSATION_PRIORITY = {
  URGENT: 'urgent',
  HIGH: 'high',
  LOW: 'low',
  MEDIUM: 'medium',
};

export const CONVERSATION_PRIORITY_ORDER = {
  urgent: 4,
  high: 3,
  medium: 2,
  low: 1,
};

// Claudia event types — aligned with ClaudiaEvent sealed interface
export const CONVERSATION_EVENT_TYPE = {
  INTENT_CHANGED: 'INTENT_CHANGED',
  FLOW_START: 'FLOW_START',
  FLOW_END: 'FLOW_END',
  FLOW_ERROR: 'FLOW_ERROR',
  N2_TRANSFER: 'N2_TRANSFER',
  // claudia-agentic supervisor/subagent lifecycle — shared verbatim across
  // cloudchat / ch-connector / claudia-agentic.
  SUPERVISOR_TRIGGERED: 'SUPERVISOR_TRIGGERED',
  SUBAGENT_CALLED: 'SUBAGENT_CALLED',
  SUBAGENT_RETURNED: 'SUBAGENT_RETURNED',
  // Tool calls a subagent makes — nested under the subagent in the turn tree
  // (AB#7647 v2). Never rendered as standalone activity rows.
  TOOL_CALLED: 'TOOL_CALLED',
  TOOL_RETURNED: 'TOOL_RETURNED',
};

const lookupMap = entries => Object.assign(Object.create(null), entries);

export const MESSAGE_EVENT_TYPE = {
  CLARIFICATION: 'CLARIFICATION',
  NO_VALID_CONTENT: 'NO_VALID_CONTENT',
  ENLIGHTENMENT_QUESTION_LIMIT: 'ENLIGHTENMENT_QUESTION_LIMIT',
  COUNT_LIMIT: 'COUNT_LIMIT',
  // Per-turn detectors (AB#7890): both score every turn and, until now, only
  // their outcome was visible — a bare N2_TRANSFER with no counter behind it.
  FRUSTRATION: 'FRUSTRATION',
  FORWARD_TO_HUMAN: 'FORWARD_TO_HUMAN',
};

// The forward-to-human row says what the router did, because the counters alone
// cannot: an engagement bypass escalates with `current` still below `limit`.
export const FORWARD_TO_HUMAN_OUTCOME_LABELS = lookupMap({
  RETAINING: 'CONVERSATION.CONTEXT.FORWARD_TO_HUMAN_RETAINING',
  THRESHOLD_REACHED: 'CONVERSATION.CONTEXT.FORWARD_TO_HUMAN_THRESHOLD_REACHED',
  ENGAGEMENT_BYPASS: 'CONVERSATION.CONTEXT.FORWARD_TO_HUMAN_ENGAGEMENT_BYPASS',
});

export const CONVERSATION_EVENT_LABELS = lookupMap({
  [CONVERSATION_EVENT_TYPE.INTENT_CHANGED]:
    'CONVERSATION.CONTEXT.INTENT_CHANGED',
  [CONVERSATION_EVENT_TYPE.FLOW_START]: 'CONVERSATION.CONTEXT.FLOW_START',
  [CONVERSATION_EVENT_TYPE.FLOW_END]: 'CONVERSATION.CONTEXT.FLOW_END',
  [CONVERSATION_EVENT_TYPE.FLOW_ERROR]: 'CONVERSATION.CONTEXT.FLOW_ERROR',
  [CONVERSATION_EVENT_TYPE.N2_TRANSFER]: 'CONVERSATION.CONTEXT.N2_TRANSFER',
  [CONVERSATION_EVENT_TYPE.SUPERVISOR_TRIGGERED]:
    'CONVERSATION.CONTEXT.SUPERVISOR_TRIGGERED',
  [CONVERSATION_EVENT_TYPE.SUBAGENT_CALLED]:
    'CONVERSATION.CONTEXT.SUBAGENT_CALLED',
  [CONVERSATION_EVENT_TYPE.SUBAGENT_RETURNED]:
    'CONVERSATION.CONTEXT.SUBAGENT_RETURNED',
});

export const CONVERSATION_EVENT_ICONS = lookupMap({
  [CONVERSATION_EVENT_TYPE.INTENT_CHANGED]: 'chat-alt-6',
  [CONVERSATION_EVENT_TYPE.FLOW_START]: 'thunderbolt',
  [CONVERSATION_EVENT_TYPE.FLOW_END]: 'thunderbolt',
  [CONVERSATION_EVENT_TYPE.FLOW_ERROR]: 'dismiss-circle',
  [CONVERSATION_EVENT_TYPE.N2_TRANSFER]: 'message-switch',
  // AB#7647 "Option B" — supervisor/orchestration uses `controlled-flows`
  // (closest fluent-icon-v2 glyph to the design's `flowchart`, which is absent
  // from the set); subagent rows use `bot`; the per-row OK/error status icons
  // (`checkmark-circle`/`error-circle`) live in AgenticTurnBlock, not here.
  [CONVERSATION_EVENT_TYPE.SUPERVISOR_TRIGGERED]: 'controlled-flows',
  [CONVERSATION_EVENT_TYPE.SUBAGENT_CALLED]: 'bot',
  [CONVERSATION_EVENT_TYPE.SUBAGENT_RETURNED]: 'bot',
});

export const MESSAGE_EVENT_LABELS = lookupMap({
  [MESSAGE_EVENT_TYPE.CLARIFICATION]: 'CONVERSATION.CONTEXT.CLARIFICATION',
  [MESSAGE_EVENT_TYPE.NO_VALID_CONTENT]:
    'CONVERSATION.CONTEXT.NO_VALID_CONTENT',
  [MESSAGE_EVENT_TYPE.ENLIGHTENMENT_QUESTION_LIMIT]:
    'CONVERSATION.CONTEXT.ENLIGHTENMENT_QUESTION_LIMIT',
  [MESSAGE_EVENT_TYPE.COUNT_LIMIT]: 'CONVERSATION.CONTEXT.COUNT_LIMIT',
  [MESSAGE_EVENT_TYPE.FRUSTRATION]: 'CONVERSATION.CONTEXT.FRUSTRATION',
  // Fallback for a forward-to-human row with no (or an unknown) outcome.
  [MESSAGE_EVENT_TYPE.FORWARD_TO_HUMAN]:
    'CONVERSATION.CONTEXT.FORWARD_TO_HUMAN',
});

export const MESSAGE_EVENT_ICONS = lookupMap({
  [MESSAGE_EVENT_TYPE.CLARIFICATION]: 'chat-help',
  [MESSAGE_EVENT_TYPE.NO_VALID_CONTENT]: 'error-circle',
  [MESSAGE_EVENT_TYPE.ENLIGHTENMENT_QUESTION_LIMIT]: 'chat-multiple',
  [MESSAGE_EVENT_TYPE.COUNT_LIMIT]: 'warning',
  [MESSAGE_EVENT_TYPE.FRUSTRATION]: 'alert',
  [MESSAGE_EVENT_TYPE.FORWARD_TO_HUMAN]: 'person',
});

// Size in mega bytes
export const MAXIMUM_FILE_UPLOAD_SIZE = 40;
export const MAXIMUM_FILE_UPLOAD_SIZE_TWILIO_SMS_CHANNEL = 5;
export const MAXIMUM_VIDEO_FILE_UPLOAD_SIZE_WHATSAPP = 16;
export const MAXIMUM_CONTACT_IMPORT_FILE_SIZE = 10;

export const ALLOWED_FILE_TYPES =
  'image/*,' +
  'audio/*,' +
  'video/*,' +
  '.3gpp,' +
  'text/csv, text/plain, application/json, application/pdf, text/rtf,' +
  'application/zip, application/x-7z-compressed application/vnd.rar application/x-tar,' +
  'application/msword, application/vnd.ms-excel, application/vnd.ms-powerpoint, application/vnd.oasis.opendocument.text,' +
  'application/vnd.openxmlformats-officedocument.presentationml.presentation, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,' +
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document,';

export const ALLOWED_FILE_TYPES_FOR_TWILIO_WHATSAPP =
  'image/png, image/jpeg,' +
  'audio/mpeg, audio/opus, audio/ogg, audio/amr,' +
  'video/mp4,' +
  'application/pdf,';
// https://developers.line.biz/en/reference/messaging-api/#image-message, https://developers.line.biz/en/reference/messaging-api/#video-message
export const ALLOWED_FILE_TYPES_FOR_LINE = 'image/png, image/jpeg,video/mp4';

export const getAllowedFileTypes = () => {
  if (typeof window.chatwootWebChannel === 'undefined') {
    return '';
  }
  const { allowAnyFiletypeOnWebWidgetUpload } = window.chatwootWebChannel;
  return allowAnyFiletypeOnWebWidgetUpload ? '' : ALLOWED_FILE_TYPES;
};

export const CSAT_RATINGS = [
  {
    key: 'disappointed',
    translationKey: 'CSAT.RATINGS.POOR',
    emoji: '1️⃣😞',
    value: 1,
    color: '#FDAD2A',
  },
  {
    key: 'expressionless',
    translationKey: 'CSAT.RATINGS.FAIR',
    emoji: '2️⃣😑',
    value: 2,
    color: '#FFC532',
  },
  {
    key: 'neutral',
    translationKey: 'CSAT.RATINGS.AVERAGE',
    emoji: '3️⃣😐',
    value: 3,
    color: '#FCEC56',
  },
  {
    key: 'grinning',
    translationKey: 'CSAT.RATINGS.GOOD',
    emoji: '4️⃣😀',
    value: 4,
    color: '#6FD86F',
  },
  {
    key: 'smiling',
    emoji: '5️⃣😍',
    translationKey: 'CSAT.RATINGS.EXCELLENT',
    value: 5,
    color: '#44CE4B',
  },
];

export const AUDIO_FORMATS = {
  WEBM: 'audio/webm',
  OGG: 'audio/ogg',
  MP3: 'audio/mp3',
  WAV: 'audio/wav',
};

// Set `serverOnly: true` on tokens that only the backend Liquid renderer can
// resolve (or that ReplyBox handles via a dedicated regex pre-substitution).
// ReplyBox derives its "skip undefined-vars dialog" allow-list from this flag
// — see `serverOnlyMessageVariables` in ReplyBox.vue — so a new server-only
// token becomes safe to send by flagging it here, no second list to keep.
export const MESSAGE_VARIABLES = [
  {
    label: 'Conversation Display Id',
    key: 'conversation.display_id',
    serverOnly: true,
  },
  {
    label: 'Conversation Id',
    key: 'conversation.id',
  },
  {
    label: 'Contact Id',
    key: 'contact.id',
  },
  {
    label: 'Contact name',
    key: 'contact.name',
  },
  {
    label: 'Contact first name',
    key: 'contact.first_name',
  },
  {
    label: 'Contact last name',
    key: 'contact.last_name',
  },
  {
    label: 'Contact email',
    key: 'contact.email',
  },
  {
    label: 'Contact phone',
    key: 'contact.phone',
  },
  {
    label: 'Agent name',
    key: 'agent.name',
  },
  {
    label: 'Agent first name',
    key: 'agent.first_name',
  },
  {
    label: 'Agent last name',
    key: 'agent.last_name',
  },
  {
    label: 'Agent email',
    key: 'agent.email',
  },
  ...['name', 'first_name', 'last_name', 'available_name'].map(field => ({
    label: `Assignee ${field.replaceAll('_', ' ')}`,
    key: `conversation.assignee.${field}`,
  })),
];

export const ATTACHMENT_ICONS = {
  image: 'image',
  audio: 'headphones-sound-wave',
  video: 'video',
  file: 'document',
  location: 'location',
  fallback: 'link',
};
